import React from 'react';
import { 
  Laptop, Monitor, Droplets, Keyboard, Zap, Database, Cpu, 
  ShieldAlert, Apple, GraduationCap, Building2, Wifi, Search, CheckCircle2,
  Battery, AlertTriangle, Speaker, Bug, Activity, Gamepad2, ShieldCheck, FileText, BookOpen, Globe, Mail, Car
} from 'lucide-react';

export interface ServiceDetail {
  id: string;
  title: string;
  category: 'hardware' | 'software' | 'brand' | 'local' | 'data';
  icon: any;
  suburbContext?: string;
  description: string;
  extendedDescription?: string[];
  masterTip: string;
  commonSymptoms: string[];
  postcodes?: string[];
  images?: string[];
  heroImage?: string;
  heroVideo?: string;
  customPrice?: boolean;
  customPriceText?: string;
  customPriceSubtext?: string;
  expertArticles?: {
    title: string;
    content: string[];
  }[];
}

export const SERVICE_DETAILS: Record<string, ServiceDetail> = {
  'laptop-hinge-repairs': {
    id: 'laptop-hinge-repairs',
    title: 'Laptop Hinge Repairs (Laptop Top Casing Replacement)',
    category: 'hardware',
    icon: Laptop,
    description: "Expert laptop hinge repair and top casing replacement to fix broken, loose, or separated screens and prevent further display damage.",
    masterTip: "If your laptop hinge feels stiff or makes a cracking noise, stop forcing it closed! Forcing a broken hinge can snap the screen or tear the display cable.",
    commonSymptoms: ["Screen separating from base", "Cracking noise when opening", "Loose or wobbly display"]
  },
  // Gaming PC
  'gaming-pc-repairs': {
    id: 'gaming-pc-repairs',
    title: 'Gaming PC Repairs & Upgrades',
    category: 'hardware',
    icon: Cpu,
    description: "Same Day Custom Built Gaming PC & Home Computer Repairs. We fix Motherboard Repairs, Bios Fix, Black Screen, Not Turning on, Blue Screen of Dead, Dram Error, Cpu Error, VGA error, Boot Error, Bios Screen, No Post, CPU fan Error, Liquid Cpu cooler Error, Overheating Gaming PC, Low Performance PC fix, Thermal Paste Replacement, Maintenance, Gaming PC Upgrade, Case Swap, Performance Upgrade, Cooling Upgrade, Complete System Clone Copy, Data Recovery and more with comprehensive repairs.",
    extendedDescription: [
      "Whether you have a Custom Built Gaming PC or a pre-built system, we offer comprehensive repairs and upgrades. We diagnose and fix complex issues like Motherboard Repairs, Bios Fixes, Black Screens, and systems Not Turning on.",
      "Experiencing a Blue Screen of Dead, Dram Error, Cpu Error, VGA error, Boot Error, Bios Screen, or No Post? We have the tools and expertise to identify the root cause and get you back in the game Same Day.",
      "Don't let an Overheating Gaming PC ruin your performance. We resolve CPU fan Errors, Liquid Cpu cooler Errors, and Low Performance PC issues with professional Thermal Paste Replacement, Cooling Upgrades, and routine Maintenance.",
      "Looking to boost your FPS? We provide Gaming PC Upgrades, Case Swaps, Performance Upgrades, Complete System Clone Copies, and Data Recovery to ensure your rig runs at peak performance."
    ],
    masterTip: "Regular Maintenance and Thermal Paste Replacement can prevent an Overheating Gaming PC and extend the life of your Custom Built Gaming PC.",
    commonSymptoms: ["Black Screen", "Blue Screen of Dead", "Dram Error", "Cpu Error", "VGA error", "Boot Error", "No Post", "Overheating Gaming PC"],
    images: [
      "https://i.imgur.com/GFKRUxE.jpeg",
      "https://i.imgur.com/QoN1cJ3.jpeg",
      "https://i.imgur.com/xRTw3EA.jpeg",
      "https://i.imgur.com/XL0KPbS.jpeg",
      "https://i.imgur.com/GF29s97.jpeg",
      "https://i.imgur.com/h7m4cCH.jpeg"
    ],
    heroVideo: "https://player.vimeo.com/video/1176865265?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1",
    expertArticles: [
      {
        title: "Why Surge Protected Power Boards Are Crucial for Your Precious Gaming PC",
        content: [
          "Hey there! Let's talk about power. You've invested a lot of hard-earned cash into your custom gaming rig, so plugging it straight into the wall is a huge risk.",
          "Power surges from storms, grid fluctuations, or even just turning on a high-draw appliance in your house can send a spike of electricity straight into your PC. This can fry your motherboard, GPU, or power supply in a split second.",
          "A good quality surge-protected power board acts as a shield, taking the hit so your precious components don't have to. It's the cheapest insurance policy you can buy for your setup!"
        ]
      },
      {
        title: "How Often Do You Need to Maintain and Service Your Gaming PC?",
        content: [
          "I get asked this all the time. As a rule of thumb, you should give your PC a basic clean (blowing out dust) every 3 to 6 months.",
          "But for a deep service—like replacing the thermal paste on your CPU and GPU, checking cable integrity, and updating your BIOS—aim for once a year.",
          "Thermal paste dries out over time, leading to overheating. A yearly checkup keeps your temps low, your fans quiet, and your frame rates high."
        ]
      },
      {
        title: "How to Keep Your Gaming PC Dust Free",
        content: [
          "Dust is the silent killer of gaming PCs. It clogs up your fans and acts like a blanket, trapping heat inside your components.",
          "To keep it at bay, keep your PC off the carpet! Put it on a desk or a hard stand. Make sure your case has positive air pressure (more intake fans than exhaust fans) so dust isn't sucked in through the unfiltered cracks.",
          "Grab a can of compressed air every few months to blow out the filters and heatsinks. Just remember to hold the fans still so they don't spin too fast and break!"
        ]
      },
      {
        title: "Why Am I Getting Low FPS on My Gaming PC?",
        content: [
          "Nothing ruins a gaming session like stuttering frame rates. If your FPS suddenly drops, the most common culprit is thermal throttling—your PC is getting too hot and slowing itself down to survive.",
          "Other sneaky causes include outdated graphics drivers, background apps hogging your CPU, or even a game update that reset your graphics settings to 'Ultra' without you noticing.",
          "Keep your drivers updated, close unnecessary background apps, and check your temps while gaming. If it's running hot, it might be time for a repaste!"
        ]
      },
      {
        title: "My PC Freezing in the Middle of a Game",
        content: [
          "A hard freeze mid-match is incredibly frustrating. This usually points to a hardware instability rather than just a software bug.",
          "It could be failing RAM, a dying power supply (PSU) that can't handle the power spikes of your GPU, or an unstable overclock. Overheating can also cause a hard lockup.",
          "If your PC completely locks up and you have to hold the power button to restart, it's time to run some diagnostic tests on your memory and check your power delivery. Bring it in, and we can test the components individually."
        ]
      },
      {
        title: "How to Make Sure Your Gaming PC is Not Bloated with Malwares and Adwares",
        content: [
          "You want all your system resources dedicated to your games, not background junk. To keep your PC lean and mean, avoid downloading 'free' game mods or software from sketchy sites.",
          "Stick to Windows Defender—it's actually fantastic nowadays—and run a scan with Malwarebytes occasionally if you suspect something is wrong.",
          "Also, check your Task Manager's 'Startup' tab and disable any apps you don't need launching when you turn on your PC. Less bloat means more frames!"
        ]
      }
    ]
  },
  // Screen & Display
  'laptop-screen-replacement': {
    id: 'laptop-screen-replacement',
    title: 'Laptop Screen Replacement Brisbane',
    category: 'hardware',
    icon: Monitor,
    description: "Ike specializes in precision screen replacements for all major brands. Whether it's a cracked LCD, flickering backlight, or dead pixels, we use only high-grade OEM or genuine panels with a full Australian warranty.",
    extendedDescription: [
      "We service all major laptop brands including Apple MacBook, HP, Dell, Lenovo, Asus, and Acer. Please note that we do not replace displays on Microsoft Surface laptops due to difficulties in accessing original, reliable part supplies. Whether you have a standard business laptop or a high-end gaming rig, we source the exact OEM replacement panel required for your specific model.",
      "Modern laptops come with a variety of screen technologies. We replace standard LCD and LED panels, high-resolution 4K and Retina displays, as well as vibrant OLED screens. Important note: Most touch screen laptops with pen support might not work with a standard replacement display. Please let us know if you have a pen active support display, so we can source specifically for pen support display to ensure full functionality.",
      "It's important to note that Apple MacBooks and many modern HP laptops feature incredibly thin display assemblies. These screens are highly sensitive to physical impacts and pressure. Even a small crumb or pen left on the keyboard when closing the lid can cause the glass to crack or the internal LCD to bleed. We provide specialized care and precision alignment when repairing these delicate devices to prevent future stress fractures."
    ],
    masterTip: "Modern screens are extremely thin. Never leave a pen or small object on your keyboard before closing the lid—it's the #1 cause of cracked panels in Brisbane.",
    commonSymptoms: ["Cracked glass", "Vertical colored lines", "Black spots (LCD leakage)", "Flickering image"],
    postcodes: ['4068', '4066', '4069'],
    images: [
      "https://i.imgur.com/zCRK9Ec.jpeg",
      "https://i.imgur.com/j7ZErIk.jpeg",
      "https://i.imgur.com/pbOaJTx.jpeg",
      "https://i.imgur.com/c3zFXls.jpeg"
    ],
    heroImage: "https://i.imgur.com/HY7OBj8.png",
    expertArticles: [
      {
        title: "How to Open Your Laptop Display (Preventing Broken Hinges)",
        content: [
          "One of the most common causes of a cracked screen isn't dropping the laptop—it's opening it incorrectly! Always open your laptop display from the top center, right above the webcam.",
          "If you pull from one of the top corners, you create an uneven twisting force. Over time, this stress causes the top casing hinge sockets to fail. In most cases, the top case hinge sockets break, which cracks the display and leads to the hinges detaching from the display and the casing.",
          "By simply opening the lid from the middle, you distribute the tension evenly across both hinges, preventing a costly laptop screen replacement and broken hinge repair."
        ]
      },
      {
        title: "School Laptops & Touch Displays: Most Frequent Crack Scenarios",
        content: [
          "School laptops take a beating. The most frequent crack display scenarios we see involve backpacks. Students often stuff their laptop into a bag alongside heavy textbooks, applying immense pressure to the center of the lid.",
          "Another common scenario is closing the lid while a pen, earbud, or even a thick USB drive is resting on the keyboard. With modern touch displays, the glass digitizer shatters instantly under that focused pressure.",
          "To prevent physical damages, always use a padded, rigid sleeve before putting the laptop in a backpack, and double-check the keyboard before slamming the lid shut."
        ]
      },
      {
        title: "Why Recent MacBooks Are Extremely Fragile",
        content: [
          "Apple's recent MacBook models are engineering marvels, but they are also extremely fragile and hard to keep from cracking. The clearance between the screen and the keyboard when closed is practically zero.",
          "Because of this tight tolerance, using a hard plastic keyboard cover or a sliding webcam cover is highly dangerous. We've seen hundreds of MacBooks come in for screen replacement simply because a tiny plastic webcam cover shattered the display when the lid was closed.",
          "Keep your MacBook bare. Don't use screen protectors or keyboard covers, and always ensure the keyboard deck is completely clear of debris before closing it."
        ]
      },
      {
        title: "Chronic Display Problems with Specific Models and Brands",
        content: [
          "Some laptops have chronic display problems due to design flaws. For example, older MacBooks suffered from 'Flexgate', where the delicate display cable would wear out from normal opening and closing, causing a stage-light effect or a completely black screen.",
          "Certain consumer-grade HP and Lenovo models are notorious for weak hinge mounts. The plastic standoffs inside the bottom case snap, causing the hinge to seize up and eventually crack the screen bezel or the LCD panel itself.",
          "If your laptop hinge feels unusually stiff or makes a clicking noise, bring it in immediately. Loosening the hinge nuts and repairing the mounts early can save you from needing a full laptop screen replacement later."
        ]
      },
      {
        title: "How to Choose the Best Durable Laptop Case",
        content: [
          "A good case is your first line of defense against physical damage. When choosing a durable laptop case, look for rigid edges and thick corner bumpers. Drops usually impact the corners, sending a shockwave that shatters the screen.",
          "Avoid thin neoprene sleeves; they protect against scratches but do nothing against drops or pressure. Instead, opt for a hard-shell EVA sleeve or a backpack with a suspended laptop compartment that keeps the device from hitting the ground when you set your bag down.",
          "Remember, a $50 heavy-duty case is much cheaper than a $400 laptop screen replacement!"
        ]
      },
      {
        title: "How to Find a Reliable Laptop Screen Replacement Technician",
        content: [
          "Finding a reliable laptop screen replacement repair technician means looking beyond the cheapest price. A cheap repair often means a low-quality aftermarket screen with poor color accuracy, lower brightness, or dead pixels.",
          "Always ask if the technician uses OEM (Original Equipment Manufacturer) or high-grade compatible panels. Check their reviews for mentions of warranty support and turnaround times.",
          "At Ike's, we pride ourselves on transparency. We source the exact panel your laptop needs, test it thoroughly for dead pixels, and back our work with a solid warranty."
        ]
      },
      {
        title: "What is the Cost for Laptop Screen Replacement & How the Repair Process Works",
        content: [
          "The cost for a laptop screen replacement varies widely depending on the exact panel. A standard 1080p non-touch PC screen might cost $100-$150 for the part, while a high-end OLED, 4K touch display, or MacBook Retina assembly can run from $300 to over $600.",
          "Our repair process is simple and transparent: We charge a fixed $170 labor fee plus the exact cost of the replacement part. First, we diagnose the issue and find the exact part number behind your current screen.",
          "Once the part arrives (often next-day), we carefully disassemble the display assembly, replace the broken panel, test the webcam, microphone, and touch functionality, and ensure the hinges are perfectly aligned before returning it to you."
        ]
      }
    ]
  },
  'visual-glitch-repair': {
    id: 'visual-glitch-repair',
    title: 'Visual Glitch & GPU Repair',
    category: 'hardware',
    icon: Monitor,
    description: "Visual artifacts, pink lines, or random black screens often indicate a failing Graphics Processing Unit (GPU) or a damaged LVDS display cable. We diagnose these complex video faults.",
    masterTip: "If you see artifacts (strange shapes) only when playing games, your GPU might be overheating. A thermal paste repaste can often save the card before it dies permanently.",
    commonSymptoms: ["Pink or green lines on screen", "Games crashing to desktop", "Strange polygon shapes (artifacts)", "Screen goes black but computer stays on"],
  },
  'cracked-lcd-repair-4068': {
    id: 'cracked-lcd-repair-4068',
    title: 'Cracked LCD Repair 4068',
    category: 'hardware',
    icon: Search,
    suburbContext: 'Indooroopilly',
    description: "Local, fast LCD repairs for Indooroopilly residents. Skip the mall queues and get a fixed-price $170 labor quote from a master technician.",
    masterTip: "If your screen is cracked but still works, back up your data immediately. Cracks can spread and eventually cause the touch digitizer to ghost-click, potentially locking you out.",
    commonSymptoms: ["Internal bleeding in the display", "Glass shards", "Touch screen not responding"],
  },

  // Peripherals & Liquid
  'keyboard-replacement': {
    id: 'keyboard-replacement',
    title: 'Keyboard Replacement',
    category: 'hardware',
    icon: Keyboard,
    description: "Stuck keys? Missing caps? We replace full keyboard assemblies for MacBooks (top cases) and Windows laptops.",
    masterTip: "Many modern keyboards are riveted into the frame. Replacing them requires removing every single internal component. It's a precision job best left to a lab environment.",
    commonSymptoms: ["Keys typing multiple letters", "Missing keycaps", "Backlight not working"],
  },
  'water-damage-repair': {
    id: 'water-damage-repair',
    title: 'Water & Liquid Damage Repair',
    category: 'hardware',
    icon: Droplets,
    description: "Advanced logic board cleaning and component-level microsoldering to reverse corrosion caused by spills.",
    masterTip: "The 'Rice' trick is a myth. It traps moisture and accelerates corrosion. Power off immediately and call a professional for an ultrasonic clean.",
    commonSymptoms: ["Will not turn on", "Keyboard keys acting up", "Erratic fan behavior"],
  },
  'macbook-liquid-damage-repair': {
    id: 'macbook-liquid-damage-repair',
    title: 'MacBook Liquid Damage Repair',
    category: 'brand',
    icon: Apple,
    description: "Specialized recovery for Apple Silicon (M1/M2/M3) boards. We focus on saving your data first, then restoring functionality.",
    extendedDescription: [
      "Liquid damage is one of the most common and destructive issues a MacBook can face. Whether it's a spilled cup of coffee, water, or wine, liquid immediately begins corroding the delicate components on your logic board.",
      "Our MacBook Liquid Damage Repair service specializes in component-level microsoldering and ultrasonic cleaning. We don't just replace the entire expensive logic board like the Apple Store does; we find the specific shorted components, replace them, and restore power to your original board.",
      "This approach is crucial for Apple Silicon (M1, M2, M3) MacBooks, where the SSD storage is soldered directly to the board. If the board is dead, your data is trapped. By repairing the board itself, we prioritize saving your precious photos, documents, and business files while getting your MacBook running again."
    ],
    masterTip: "Apple Silicon MacBooks store data on the logic board. If the board is dead, the data is gone—unless a microsoldering specialist like Ike can restore power to the NAND chips.",
    commonSymptoms: ["Coffee spill", "Wine/Beer damage", "Total power failure", "Sticky keys", "Trackpad not clicking"],
    images: [
      "https://i.imgur.com/Y2UFLnm.jpeg",
      "https://i.imgur.com/W18NIrq.jpeg",
      "https://i.imgur.com/t1m1j2U.jpeg",
      "https://i.imgur.com/7nUM1G5.jpeg",
      "https://i.imgur.com/nuhCcdF.jpeg",
      "https://i.imgur.com/nKl47VY.jpeg",
      "https://i.imgur.com/4C0DXWy.jpeg",
      "https://i.imgur.com/NtUoAdV.jpeg",
      "https://i.imgur.com/ig0BRIm.jpeg"
    ],
    heroImage: "https://i.imgur.com/Y2UFLnm.jpeg",
    expertArticles: [
      {
        title: "How to Prevent Spill Damage and Water Damage to Your Mac",
        content: [
          "Preventing MacBook liquid damage is always cheaper than repairing it. The most common cause of a wet MacBook logic board is a knocked-over coffee cup or water bottle on the same desk. Always keep liquids at least an arm's length away from your Apple device, or use spill-proof tumblers.",
          "If you commute, ensure your water bottle is completely sealed and kept in a separate compartment from your MacBook Pro or MacBook Air. A leaky bottle in a backpack is a silent killer for electronics.",
          "While using a silicone keyboard cover can offer minor protection against tiny splashes, it won't save your logic board from a major spill. Furthermore, leaving a keyboard cover on when closing your MacBook can crack the fragile Retina display. The best defense is simply keeping liquids off your workstation."
        ]
      },
      {
        title: "Critical First Steps: What to Do Once Your Mac is Liquid Damaged",
        content: [
          "If you just spilled water, coffee, tea, or wine on your MacBook, the first 60 seconds are absolutely critical to its survival. Step one: Disconnect the MagSafe charger or USB-C power cable immediately to prevent a massive power surge.",
          "Step two: Turn off the MacBook immediately. Do not use the mouse to shut it down; press and hold the power button (or Touch ID button) for 10 seconds until the screen goes completely black and the machine powers off.",
          "Step three: Unplug all peripherals, including USB hubs, external monitors, and hard drives. Finally, place the MacBook face down on a towel in an inverted 'V' (tent) position to let gravity pull the liquid away from the logic board. Do NOT try to turn it back on to 'see if it works.' Sending electricity through a wet logic board is the number one cause of permanent MacBook water damage and data loss."
        ]
      },
      {
        title: "What is Critical When Your Mac is Wet (And Why the Rice Trick is a Myth)",
        content: [
          "The most critical factor in MacBook liquid damage repair is time. Corrosion begins the exact moment liquid meets the internal copper traces and components. Sugary drinks like soda or acidic liquids like wine accelerate this corrosion exponentially.",
          "Many people try putting their wet MacBook in a bag of rice, but this is a dangerous myth. Rice does not absorb moisture from inside the chassis; instead, it introduces dust, starch, and debris into the ports and fans, accelerating logic board corrosion and trapping moisture inside.",
          "The only proven method to fix a water-damaged MacBook is a professional ultrasonic cleaning and component-level microsoldering. Bring your device to a MacBook liquid damage repair specialist immediately so we can open the chassis, disconnect the internal battery, and safely neutralize the liquid before it destroys your data."
        ]
      }
    ]
  },
  'macbook-screen-replacement': {
    id: 'macbook-screen-replacement',
    title: 'MacBook Screen Replacement Brisbane',
    category: 'brand',
    icon: Monitor,
    description: "Expert, precision-driven MacBook screen replacements. We restore your Retina display with OEM-grade panels, ensuring True Tone functionality and flawless camera alignment.",
    extendedDescription: [
      "When your MacBook display is cracked, flickering, or showing vertical lines, you need a specialist. Apple's Retina displays are incredibly thin and complex, housing delicate flex cables, the FaceTime camera, and ambient light sensors. A generic repair can result in lost True Tone functionality, poor backlight uniformity, or even logic board shorts. At Ike's Computer Repairs, we bring years of specialized experience to every MacBook screen replacement, ensuring a factory-finish result.",
      "Our expertise covers the entire MacBook lineup, from the classic Intel models to the latest Apple Silicon M1, M2, and M3 MacBook Air and MacBook Pro series. We source only the highest quality OEM-grade display assemblies. This means your new screen will have the exact same color accuracy, brightness, and resolution as the day you bought it. We meticulously transfer or program necessary ICs to retain critical features like True Tone and auto-brightness.",
      "Trust is paramount when handing over your premium device. We operate with complete transparency: our labor fee is a fixed $170, and we provide upfront quotes for the exact display assembly your specific MacBook model requires. All our MacBook screen replacements are backed by a comprehensive 12-month warranty. As Brisbane's trusted independent Apple specialists, we guarantee a 'No Fix, No Fee' policy, giving you complete peace of mind."
    ],
    masterTip: "Never close your MacBook with a webcam cover attached. The clearance between the glass and the keyboard is engineered to microscopic tolerances, and even a thin plastic cover will crack the display.",
    commonSymptoms: ["Cracked glass or LCD bleeding", "Vertical colored lines", "Backlight completely dark (Flexgate)", "Flickering when adjusting the screen angle"],
    postcodes: ['4000', '4005', '4006'],
    images: [
      "https://i.imgur.com/zCRK9Ec.jpeg",
      "https://i.imgur.com/j7ZErIk.jpeg",
      "https://i.imgur.com/pbOaJTx.jpeg",
      "https://i.imgur.com/c3zFXls.jpeg"
    ],
    heroVideo: "https://player.vimeo.com/video/1180076769?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1",
    expertArticles: [
      {
        title: "The Importance of True Tone Restoration",
        content: [
          "Modern MacBooks use an ambient light sensor to adjust the color temperature of your display to match your environment—a feature Apple calls True Tone.",
          "During a screen replacement, if the new display is not properly calibrated or if the original sensor data isn't transferred, True Tone will be permanently disabled by macOS.",
          "Our technicians use specialized programming tools to read the serial data from your original damaged screen and write it to the replacement panel, ensuring your MacBook functions exactly as Apple intended."
        ]
      },
      {
        title: "Flexgate: Is it the Screen or the Cable?",
        content: [
          "If your MacBook Pro (specifically 2016-2018 models) has a backlight that cuts out when you open the lid past a certain angle, or shows a 'stage light' effect at the bottom, you might be experiencing 'Flexgate'.",
          "This is caused by the delicate backlight flex cable wearing out from repeated opening and closing. Because the cable is integrated into the screen, the official fix requires a full display assembly replacement.",
          "We can properly diagnose whether your issue is a simple flex cable fault or a logic board backlight circuit failure before proceeding with a screen replacement."
        ]
      }
    ]
  },

  // Power & Battery
  'laptop-wont-turn-on': {
    id: 'laptop-wont-turn-on',
    title: "Laptop Won't Turn On",
    category: 'hardware',
    icon: Zap,
    description: "Deep diagnostics to find the point of failure. It could be a dead charging port, a failed power rail, or a shorted capacitor.",
    masterTip: "Check your charger first. Many 'dead' laptops are simply being fed by a faulty power brick or a damaged DC jack pin.",
    commonSymptoms: ["No lights when charging", "Clicks but doesn't start", "Fans spin then stop"],
  },
  'power-supply-replacement': {
    id: 'power-supply-replacement',
    title: 'Desktop Power Supply Replacement',
    category: 'hardware',
    icon: Zap,
    description: "If your desktop PC is completely dead after a storm or just won't start, the PSU likely failed. We replace it with a Gold-rated unit for stability.",
    masterTip: "Never reuse the modular cables from your old power supply with a new one! The pinouts are different and you will fry your motherboard instantly.",
    commonSymptoms: ["No power LEDs", "Smoke smell from back of PC", "PC shuts down under load"],
  },
  'battery-replacement-macbook': {
    id: 'battery-replacement-macbook',
    title: 'Battery Replacement MacBook',
    category: 'brand',
    icon: Battery,
    description: "Restoring the portability of your MacBook Air or Pro with high-cycle count, safe batteries.",
    masterTip: "MacBook batteries are glued in. Attempting to pry them without the correct chemical solvent can puncture the cell and cause a thermal event.",
    commonSymptoms: ["'Service Recommended' warning", "Shutting down at 20%", "Trackpad feels tight"],
  },
  'dc-jack-repair': {
    id: 'dc-jack-repair',
    title: 'DC Jack & Charging Port Repair',
    category: 'hardware',
    icon: Zap,
    description: "Fixing loose or broken charging ports. We resolder or replace the physical DC jack to ensure stable power delivery.",
    masterTip: "Stop wiggling the cable to make it charge! You are arcing electricity, which can burn the motherboard layers and make it unfixable.",
    commonSymptoms: ["Charging is intermittent", "Port feels loose", "Smell of burning near charger"],
  },

  // Data & Performance
  'hard-drive-recovery': {
    id: 'hard-drive-recovery',
    title: 'Hard Drive Data Recovery Brisbane',
    category: 'data',
    icon: Database,
    description: "Forensic level recovery for failed mechanical drives and SSDs. We prioritize family photos and business critical docs.",
    masterTip: "If you hear a 'clicking' sound, turn it off. The mechanical head is scratching the platter, destroying your data every second it spins.",
    commonSymptoms: ["Clicking sounds", "I/O Device Error", "Folders showing empty"],
  },
  'hard-drive-failure': {
    id: 'hard-drive-failure',
    title: 'Hard Drive Failure Repair',
    category: 'hardware',
    icon: Database,
    description: "Diagnosis and replacement of failing hard drives. If you see 'SMART Status Bad' or 'No Boot Device', we can clone your drive to a new one before it dies completely.",
    masterTip: "Hard drives typically last 3-5 years. If yours is older and making noise, backup immediately. We can clone it to an SSD for a massive speed boost.",
    commonSymptoms: ["Slow file access", "Clicking or grinding noises", "Windows freezing randomly"],
  },
  'ssd-upgrade-brisbane': {
    id: 'ssd-upgrade-brisbane',
    title: 'SSD Upgrade Brisbane',
    category: 'hardware',
    icon: Cpu,
    description: "The single best investment for an old PC. We clone your existing system to a new, 10x faster SSD.",
    masterTip: "Upgrading to an SSD is better than buying a new budget laptop. Your old i5 processor is likely fine—it's the slow mechanical drive that is holding you back.",
    commonSymptoms: ["Taking 5 mins to boot", "Apps taking forever to open", "Constant disk usage at 100%"],
  },
  'slow-computer-fix': {
    id: 'slow-computer-fix',
    title: 'Slow Computer Diagnostics & Fix',
    category: 'software',
    icon: Activity,
    description: "We perform a deep analysis of startup items, bloatware, and thermal throttling to find out why your PC is dragging. We optimize it to run like new.",
    masterTip: "Having two antivirus programs installed doesn't make you twice as safe; it makes your computer run half as fast. They fight each other for resources.",
    commonSymptoms: ["Spinning wheel of death", "Typing lag", "Programs taking minutes to load"],
  },

  // Troubleshooting & Software
  'blue-screen-repair': {
    id: 'blue-screen-repair',
    title: 'Blue Screen & Boot Loop Repair',
    category: 'software',
    icon: AlertTriangle,
    description: "We decode the STOP error codes to find the root cause—whether it's a corrupt driver, bad RAM, or a failing update—and restore stability.",
    masterTip: "If you get a Blue Screen, write down the code at the bottom (e.g., CRITICAL_PROCESS_DIED). It tells us exactly where to start looking.",
    commonSymptoms: ["Blue screen with sad face :(", "Computer restarts automatically", "Stuck on Windows logo"],
  },
  'virus-removal-service': {
    id: 'virus-removal-service',
    title: 'Virus & Spyware Removal',
    category: 'software',
    icon: Bug,
    description: "Professional removal of malware, trojans, and remote access scams. We ensure no backdoors are left open for hackers to return.",
    masterTip: "Legitimate companies like Microsoft or Telstra will NEVER ask you to download 'AnyDesk' or 'TeamViewer' to fix your computer. Hang up immediately.",
    commonSymptoms: ["Pop-ups that won't close", "Default search engine changed", "Computer speaking to you"],
  },
  'noisy-fan-repair': {
    id: 'noisy-fan-repair',
    title: 'Noisy Fan & Thermal Repair',
    category: 'hardware',
    icon: Speaker,
    description: "Grinding, rattling, or jet-engine noises usually mean a fan bearing has failed. We replace the fan and re-paste the CPU to silence your machine.",
    masterTip: "Using compressed air on a spinning fan can over-spin the bearing and destroy it. Always hold the fan blade still while cleaning.",
    commonSymptoms: ["Loud grinding noise", "Rattling when tilted", "Computer shutting down from heat"],
  },

  // Brand Specialists
  'macbook-pro-repair-indooroopilly': {
    id: 'macbook-pro-repair-indooroopilly',
    title: 'MacBook Pro Repair Indooroopilly',
    category: 'brand',
    icon: Apple,
    suburbContext: 'Indooroopilly',
    description: "Expert MacBook Pro support near the shopping centre. We handle everything from thermal throttling to keyboard issues.",
    masterTip: "MacBook Pros need internal cleaning every 18 months. Dust build-up prevents the fans from cooling the high-performance CPU, leading to slow-downs.",
    commonSymptoms: ["Running very hot", "Slow performance", "Loud fan noise"],
  },
  'lenovo-thinkpad-specialist': {
    id: 'lenovo-thinkpad-specialist',
    title: 'Lenovo ThinkPad Specialist',
    category: 'brand',
    icon: Building2,
    description: "Specialized support for Brisbane's favorite business laptop. We stock ThinkPad keyboards and screens for fast turnaround.",
    masterTip: "ThinkPads have great spill drainage, but the sticky residue from coffee can still kill the motherboard over time. Get it cleaned professionally.",
    commonSymptoms: ["Trackpoint not working", "Charging port failure", "Keyboard sticking"],
  },

  // Local & Academic
  'priority-tech-support-students': {
    id: 'priority-tech-support-students',
    title: 'Priority Tech for St Peters & Indooroopilly High',
    category: 'local',
    icon: GraduationCap,
    suburbContext: 'Academic corridor',
    description: "Fast-track repairs for students. We prioritize your device if you have an active assignment due at local colleges.",
    masterTip: "Keep a digital backup of your assignment on OneDrive or Google Drive. If your hardware fails, you can at least finish your work on a library computer.",
    commonSymptoms: ["Screen crack during transport", "WIFI drops during exams", "Won't turn on before due date"],
  },
  'home-office-support': {
    id: 'home-office-support',
    title: 'Home Office IT Support',
    category: 'local',
    icon: Building2,
    description: "On-site tech support for professionals working from home. We handle WIFI, printer, and VPN setup to ensure you stay productive.",
    masterTip: "A mesh WIFI system or wired ethernet backhaul is usually needed for stable Zoom calls if you work far from your main router.",
    commonSymptoms: ["Zoom calls dropping", "Printer won't connect", "Slow VPN speeds"],
  },
  'mobile-computer-repairs': {
    id: 'mobile-computer-repairs',
    title: 'Mobile Computer Repairs Brisbane',
    category: 'local',
    icon: Car,
    description: "Convenient, on-site mobile computer repairs across Brisbane. We bring the repair lab to your home or office for fast, reliable tech support.",
    extendedDescription: [
      "Why pack up your heavy desktop or risk damaging your laptop further by driving it across town? Our mobile computer repair service brings expert technical support directly to your doorstep. Whether you're working from home, running a small business, or just need help setting up a new device, we provide on-site diagnostics and repairs tailored to your schedule.",
      "We handle a wide range of issues on-site, including Wi-Fi troubleshooting, printer setup, virus removal, software configuration, and hardware upgrades. For complex hardware faults like motherboard micro-soldering or extensive liquid damage, we offer a secure pickup and drop-off service to our specialized Brisbane lab.",
      "Our mobile service is built on transparency and trust. You get the same $170 fixed labor quote (plus parts) with no hidden travel fees for our standard service areas. Experience hassle-free, professional IT support without leaving your home."
    ],
    masterTip: "Before we arrive, make sure you have any necessary passwords (like your Microsoft account, Apple ID, or Wi-Fi password) written down. This saves time and gets your repair completed faster!",
    commonSymptoms: ["Wi-Fi dropping out", "Printer not connecting", "Computer running slow", "Need help setting up a new PC"],
    expertArticles: [
      {
        title: "On-Site vs. Lab Repairs: What to Expect",
        content: [
          "Most software issues, network setups, and basic hardware upgrades (like RAM or SSD installations) can be completed right at your desk.",
          "However, if your device requires component-level soldering, screen replacement, or deep liquid damage recovery, we will safely transport it to our dust-free lab environment to ensure the highest quality repair."
        ]
      }
    ]
  },
  'uq-student-laptop-repair': {
    id: 'uq-student-laptop-repair',
    title: 'UQ Student Laptop Repair Specialists',
    category: 'local',
    icon: GraduationCap,
    suburbContext: 'St Lucia / UQ',
    description: "Fast, affordable repairs for UQ students. We understand the 'Uni Budget' and offer student-friendly payment options.",
    masterTip: "Don't carry your laptop in a bag with a loose water bottle. 30% of student repairs we see are caused by water bottle leaks in backpacks.",
    commonSymptoms: ["Liquid damage", "Cracked screens", "Lost assignment data"],
  },
  'wifi-setup-brookfield': {
    id: 'wifi-setup-brookfield',
    title: 'WiFi & Home Network Setup Brookfield',
    category: 'local',
    icon: Wifi,
    suburbContext: 'Brookfield',
    description: "Full-coverage WIFI solutions for larger Brookfield properties. No more dead zones in the back shed or upstairs.",
    masterTip: "Standard ISP routers are rarely enough for Brookfield homes. Professional access points are required to push signal through thick walls and across acreage.",
    commonSymptoms: ["WIFI drops in certain rooms", "Buffering on Netflix", "Weak signal outside"],
  },
  // New SEO Services
  'laptop-fan-replacement': {
    id: 'laptop-fan-replacement',
    title: 'Laptop Fan Replacement',
    category: 'hardware',
    icon: Laptop,
    description: "Is your laptop overheating or making loud grinding noises? We provide professional laptop fan replacement to restore optimal cooling and prevent permanent thermal damage to your CPU and GPU.",
    masterTip: "Never ignore a noisy or failing fan. Overheating can quickly destroy your motherboard, turning a simple fan replacement into a costly repair.",
    commonSymptoms: ["Loud grinding noises", "Laptop shutting down randomly", "Excessive heat on the bottom case"]
  },
  'laptop-battery-replacement': {
    id: 'laptop-battery-replacement',
    title: 'Laptop Battery Replacement',
    category: 'hardware',
    icon: Zap,
    description: "If your laptop dies the moment you unplug it or the battery is swelling, it's time for a laptop battery replacement. We source high-quality OEM batteries for all major brands.",
    masterTip: "A swollen battery is a severe fire hazard. If your trackpad is popping out or your laptop case is bulging, stop using it immediately and bring it in.",
    commonSymptoms: ["Battery won't hold charge", "Laptop only works when plugged in", "Swollen or bulging case"]
  },
  'laptop-speaker-replacement': {
    id: 'laptop-speaker-replacement',
    title: 'Laptop Speaker Replacement',
    category: 'hardware',
    icon: Laptop,
    description: "Experiencing distorted, crackling, or no sound at all? Our laptop speaker replacement service restores crisp, clear audio to your device, whether it's a MacBook or a Windows PC.",
    masterTip: "Blown speakers are often caused by playing audio at maximum volume for extended periods or by liquid damage seeping through the speaker grills.",
    commonSymptoms: ["Crackling or distorted sound", "No audio output", "Muffled voices in videos"]
  },
  'laptop-ssd-replacement': {
    id: 'laptop-ssd-replacement',
    title: 'Laptop SSD Replacement',
    category: 'hardware',
    icon: Database,
    description: "Running out of storage or dealing with a failing drive? A laptop SSD replacement or upgrade is the best way to boost your storage capacity and ensure your data is safe on a reliable new drive.",
    masterTip: "If your laptop is taking more than 30 seconds to boot, upgrading from an old HDD to a modern NVMe SSD will make it feel 10x faster.",
    commonSymptoms: ["Running out of space", "Extremely slow boot times", "Clicking noises from old hard drives"]
  },
  'laptop-ram-upgrade': {
    id: 'laptop-ram-upgrade',
    title: 'Laptop RAM Upgrade',
    category: 'hardware',
    icon: Cpu,
    description: "Struggling to multitask or keep multiple browser tabs open? A laptop RAM upgrade gives your system the memory it needs to run heavy applications and games smoothly without freezing.",
    masterTip: "Check your Task Manager. If your memory usage is constantly above 80% during normal use, a RAM upgrade will significantly improve your workflow.",
    commonSymptoms: ["System freezing with multiple tabs", "Slow application switching", "100% memory usage"]
  },
  'malware-removal': {
    id: 'malware-removal',
    title: 'Malware Removal',
    category: 'software',
    icon: ShieldAlert,
    description: "Is your computer acting strangely, showing pop-ups, or running incredibly slow? Our comprehensive malware removal service eradicates hidden threats, spyware, and adware to secure your personal data.",
    masterTip: "Never click on pop-ups claiming your computer is infected. These are often the very source of the malware. Close the browser and run a legitimate scan.",
    commonSymptoms: ["Unwanted browser pop-ups", "Homepage hijacked", "Unknown programs installing themselves"]
  },
  'laptop-motherboard-replacement-repair': {
    id: 'laptop-motherboard-replacement-repair',
    title: 'Laptop Motherboard Replacement and Repair',
    category: 'hardware',
    icon: Cpu,
    description: "From liquid damage to power surges, we specialize in complex laptop motherboard replacement and component-level repair. We fix the board instead of just replacing it whenever possible to save you money.",
    masterTip: "Component-level motherboard repair requires specialized micro-soldering equipment. Don't trust this delicate work to just any repair shop.",
    commonSymptoms: ["Completely dead laptop", "No charging light", "Beeping codes on startup"]
  },
  'laptop-not-powering-up': {
    id: 'laptop-not-powering-up',
    title: 'Laptop Not Powering Up',
    category: 'hardware',
    icon: Zap,
    description: "If your laptop is not powering up at all, it could be a dead battery, a faulty charging port, or a motherboard short. We provide expert diagnostics to pinpoint the exact cause and get you back up and running.",
    masterTip: "Try a 'hard reset' first: unplug the charger, hold the power button for 30 seconds, plug it back in, and try turning it on again.",
    commonSymptoms: ["No response when pressing power", "No LED charging lights", "Completely unresponsive"]
  },
  'laptop-not-turning-on': {
    id: 'laptop-not-turning-on',
    title: 'Laptop Not Turning On',
    category: 'hardware',
    icon: Zap,
    description: "A laptop not turning on is a stressful experience, especially with important data inside. We systematically test the power supply, battery, and logic board to find the fault and repair it quickly.",
    masterTip: "If the charging light turns on but the screen stays black, shine a flashlight at the screen. If you see a faint image, your backlight has failed, not the whole laptop.",
    commonSymptoms: ["Black screen", "Fans spin but no display", "Power light flashes then dies"]
  },
  'gaming-laptop-repair': {
    id: 'gaming-laptop-repair',
    title: 'Gaming Laptop Repair',
    category: 'hardware',
    icon: Gamepad2,
    description: "High-performance gaming laptops require specialized care. Our gaming laptop repair services cover overheating issues, broken hinges, GPU faults, and screen replacements for high refresh rate displays.",
    masterTip: "Gaming laptops run much hotter than standard laptops. Using them on a bed or couch suffocates the intake vents and will quickly lead to thermal damage.",
    commonSymptoms: ["Games crashing", "Overheating", "Broken hinges from heavy lids"]
  },
  'gaming-laptop-fan-replacement': {
    id: 'gaming-laptop-fan-replacement',
    title: 'Gaming Laptop Fan Replacement',
    category: 'hardware',
    icon: Laptop,
    description: "Gaming laptops rely on powerful dual-fan systems. If one fails, your FPS will drop drastically. We offer precise gaming laptop fan replacement using high-RPM OEM parts to restore your cooling.",
    masterTip: "If your gaming laptop sounds like a jet engine but is still overheating, the thermal paste has likely dried out. We always repaste the CPU and GPU during a fan replacement.",
    commonSymptoms: ["Severe FPS drops", "Loud rattling fan noise", "Laptop chassis too hot to touch"]
  },
  'gaming-laptop-battery-replacement': {
    id: 'gaming-laptop-battery-replacement',
    title: 'Gaming Laptop Battery Replacement',
    category: 'hardware',
    icon: Zap,
    description: "Gaming laptops drain batteries fast, leading to quicker degradation. Our gaming laptop battery replacement service installs high-capacity, safe OEM batteries so you can game on the go again.",
    masterTip: "Leaving your gaming laptop plugged in 100% of the time while gaming at maximum heat degrades the battery faster. Use battery charge limiting software if your brand supports it.",
    commonSymptoms: ["Battery lasts less than an hour", "Laptop shuts off instantly when unplugged", "Swollen battery pushing up trackpad"]
  },
  'gaming-laptop-cleaning': {
    id: 'gaming-laptop-cleaning',
    title: 'Gaming Laptop Cleaning',
    category: 'hardware',
    icon: ShieldCheck,
    description: "Dust is the enemy of high frame rates. Our deep gaming laptop cleaning service includes full disassembly, heatsink unblocking, fan detailing, and premium thermal paste application for maximum performance.",
    masterTip: "Never use a vacuum cleaner on your laptop vents! The static electricity can instantly kill your motherboard. Always use compressed air while holding the fans still.",
    commonSymptoms: ["Thermal throttling", "Fans spinning at max speed constantly", "Visible dust in exhaust vents"]
  },
  // Security & Consultation
  'digital-security-check': {
    id: 'digital-security-check',
    title: 'Digital Security Check',
    category: 'software',
    icon: ShieldCheck,
    description: "Ensure your personal data, banking, and online accounts are safe from hackers. Our comprehensive digital security check identifies vulnerabilities and secures your devices against modern cyber threats.",
    masterTip: "Using the same password for multiple accounts is the biggest security risk. We can help you set up a secure password manager to keep everything locked down.",
    commonSymptoms: ["Suspicious account logins", "Unusual bank activity", "Slow device performance"]
  },
  'internet-security-consultation': {
    id: 'internet-security-consultation',
    title: 'Internet Security Consultation',
    category: 'software',
    icon: ShieldAlert,
    description: "Get expert advice on how to protect your home or business network. We assess your current setup, configure firewalls, and teach you best practices to avoid phishing and ransomware.",
    masterTip: "Your router is the front door to your digital life. Always change the default admin password and keep the firmware updated.",
    commonSymptoms: ["Unsecured Wi-Fi networks", "Frequent spam emails", "Concerns about online privacy"]
  },
  'insurance-claim-report': {
    id: 'insurance-claim-report',
    title: 'Insurance Claim Report',
    category: 'data',
    icon: FileText,
    description: "Need an official assessment for your insurance company? We provide detailed, professional insurance claim reports for dropped, liquid-damaged, or power-surged computers.",
    masterTip: "Most insurance companies require a formal diagnosis from a registered technician before approving a claim. We make this process fast and hassle-free.",
    commonSymptoms: ["Accidental drops", "Liquid spills", "Lightning strike power surges"]
  },
  'computer-damage-assessment': {
    id: 'computer-damage-assessment',
    title: 'Computer Damage Assessment',
    category: 'hardware',
    icon: Search,
    description: "Not sure if your computer is worth fixing? Our thorough computer damage assessment evaluates every component to give you a clear, honest breakdown of repair costs versus replacement.",
    masterTip: "If the repair cost exceeds 50% of the price of a new, equivalent laptop, we usually recommend replacing it. We'll always give you the most economical advice.",
    commonSymptoms: ["Major physical damage", "Multiple failing parts", "Uncertainty about repair viability"]
  },
  'computer-tutorial': {
    id: 'computer-tutorial',
    title: 'Computer Tutorial',
    category: 'software',
    icon: BookOpen,
    description: "Struggling with new software, organizing files, or just want to learn how to use your computer more efficiently? We offer patient, one-on-one computer tutorials tailored to your skill level.",
    masterTip: "Write down your questions before our session! Having a clear list ensures we cover exactly what you need to learn without feeling overwhelmed.",
    commonSymptoms: ["Frustration with new updates", "Difficulty organizing photos", "Need help with basic software"]
  },
  // Web Design & Business
  'website-design': {
    id: 'website-design',
    title: 'Website Design',
    category: 'software',
    icon: Globe,
    customPrice: true,
    customPriceText: "Custom Quote",
    customPriceSubtext: "Competitive & Affordable",
    description: "Stand out online with a modern, fast, and mobile-friendly website. Our website design services are tailored to showcase your brand and convert visitors into customers.",
    masterTip: "Over 60% of web traffic comes from mobile devices. A website that isn't optimized for phones is losing you business.",
    commonSymptoms: ["Outdated website look", "Website not working on mobile", "Low online visibility"]
  },
  'business-website': {
    id: 'business-website',
    title: 'Business Website',
    category: 'software',
    icon: Building2,
    customPrice: true,
    customPriceText: "Custom Quote",
    customPriceSubtext: "Competitive & Affordable",
    description: "Get a professional business website that acts as your 24/7 digital storefront. We build secure, SEO-optimized sites that help you rank higher on Google and attract local clients.",
    masterTip: "Your website is often the first impression a customer has of your business. Make sure it loads fast and clearly states what you do.",
    commonSymptoms: ["No online presence", "Competitors ranking higher", "Need a professional digital storefront"]
  },
  'business-website-setup': {
    id: 'business-website-setup',
    title: 'Business Website Setup',
    category: 'software',
    icon: Globe,
    customPrice: true,
    customPriceText: "Custom Quote",
    customPriceSubtext: "Competitive & Affordable",
    description: "We handle the technical headaches of getting your business online. From domain registration and hosting configuration to installing WordPress, our business website setup gets you launched quickly.",
    masterTip: "Don't let your domain name expire! Always set your domain registrar account to auto-renew to prevent losing your business identity.",
    commonSymptoms: ["Confused by hosting options", "Need help registering a domain", "Want to start a new online business"]
  },
  'local-business-website': {
    id: 'local-business-website',
    title: 'Local Business Website',
    category: 'local',
    icon: Globe,
    customPrice: true,
    customPriceText: "Custom Quote",
    customPriceSubtext: "Competitive & Affordable",
    description: "Attract customers in your specific area with a targeted local business website. We optimize your site for local search terms so people nearby can find your services easily.",
    masterTip: "Pairing a well-optimized local website with a Google Business Profile is the most effective way to dominate local search results.",
    commonSymptoms: ["Not showing up in local searches", "Want to target specific suburbs", "Need more foot traffic or local leads"]
  },
  'business-emails': {
    id: 'business-emails',
    title: 'Business Emails',
    category: 'software',
    icon: Mail,
    customPrice: true,
    customPriceText: "Custom Quote",
    customPriceSubtext: "Competitive & Affordable",
    description: "Ditch the @gmail.com address and look professional with custom business emails (e.g., info@yourdomain.com). We set up secure, reliable email hosting via Google Workspace or Microsoft 365.",
    masterTip: "Using a custom domain for your email builds instant trust with clients and prevents your messages from ending up in their spam folders.",
    commonSymptoms: ["Using personal email for business", "Emails going to spam", "Need professional team communication"]
  }
};

export const getServiceDetail = (id: string): ServiceDetail => {
  return SERVICE_DETAILS[id] || {
    id,
    title: id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    category: 'hardware',
    icon: Laptop,
    description: "Expert computer repair and technical support provided by Ike. $170 fixed labor quote available.",
    masterTip: "Maintenance is always cheaper than repair. Keep your laptop dust-free and your software updated.",
    commonSymptoms: ["Device not working as expected", "General hardware failure"],
  };
};